<script lang="ts">
    import {emphLearn} from '$lib/utils/emph-learn';
    import ListRowButton from '$lib/components/browse/list/list-row-button.svelte';
    import WordCardMobile from '$lib/components/browse/WordCard/_Mobile/WordCardMobile.svelte';
    import type {WordIdToSentences} from '$lib/data/wordIdToSentences';
    import type {UserWordsMapStore} from '$lib/classes/stores/get-user-words-map';
    import type {DisplayWords} from '$lib/stores/displayWords';
    import type {FilterSettings} from '$lib/stores/filterSettings';
    import type {LoggedIn} from '$lib/classes/stores/logged-in';
    import {Noun, Num, type Word} from '$lib/classes/data/word';
    import {Status} from '$lib/classes/data/user-word';
    import type {CurrentWord} from '$lib/stores/currentWord';
    import {type Device, DeviceType} from '$lib/stores/ui/device';
    import type {UserActionProcessor} from '$lib/classes/services/user-action-processor';
    import {getContext, mount, unmount} from 'svelte';
    import {keyBrowseNavigator, keyUserActionProcessor} from '$lib/context-keys';
    import type {BrowseNavigator} from '$lib/classes/services/browse-navigator';

    export let wordIdToSentences: WordIdToSentences
    export let userWordsMap: UserWordsMapStore
    export let currentWord: CurrentWord
    export let displayWords: DisplayWords
    export let filterSettings: FilterSettings
    export let loggedIn: LoggedIn
    export let device: Device
    export let word: Word

    const browseNavigator: BrowseNavigator = getContext(keyBrowseNavigator)
    const userActionProcessor: UserActionProcessor = getContext(keyUserActionProcessor)

    $: current = (word === $currentWord)

    $: searchTerm = $filterSettings.search.toLowerCase()

    $: rowWord = word.base.displayWord ? word.base.displayWord : word.word
    $: if (searchTerm && !word.english) rowWord = markSearchTerm(rowWord)
    $: translations = searchTerm && word.english ? markSearchTerm(word.searchedTranslations.join(', ')) : word.base.translations.join(', ')

    function markSearchTerm(string: string) {

        const specialCharsEscaped = searchTerm.replace(/[\\\[\](){}^$.|?*+]/g, match => '\\' + match)

        let regexp = new RegExp(`^${specialCharsEscaped}|\\s${specialCharsEscaped}`, 'gi')
        return string.replace(regexp, match => {
            return `<span class="emphasize">${match}</span>`
        })
    }

    $: userWord = $userWordsMap[word.id]
    $: status = userWord?.status.toString()

    function navigate() {
        browseNavigator.navigateViaWord(word)
    }

    function handleRowClick() {
        navigate()

        if ($device === DeviceType.Mobile) {
            const c = mount(
                WordCardMobile,
                {
                    target: document.querySelector('#page') ?? document.body,
                    props: {
                        currentWord: currentWord,
                        wordIdToSentences: wordIdToSentences,
                        userWordsMap: userWordsMap,
                        displayWords: displayWords,
                        filterSettings: filterSettings,
                        device: device,
                    },
                    // QUESTION problem with typing?!?
                    // @ts-ignore
                    context: new Map([
                        [keyUserActionProcessor, userActionProcessor],
                        [keyBrowseNavigator, browseNavigator]
                    ]),
                    events: {
                        close: () => unmount(c)
                    }
                }
            )
        }
    }

    function handleRowButtonClick(status: Status) {
        navigate()

        userActionProcessor.saveWord(word.id, status)

        if (status === 'LEARN') emphLearn()
    }

</script>

{#key word}

    <!-- TODO reduce elements by using div directly inside list as row..?! -->
    <!-- svelte-ignore a11y-click-events-have-key-events  a11y-no-static-element-interactions -->
    <div class="row"
         data-testid="virtual-list-row"
         class:current-row={current}
         class:first-english={word.first}
         style={`--color-accent: var(--color-${word.art});`}
         on:click={handleRowClick}
    >

        <div class="cell word no-break" class:current-word={current}>
            <!-- @formatter:off >> line break after word would insert whitespace on page -->
            {@html rowWord}{#if word instanceof Noun}<span style="font-size: .85em">, {word.genus.article}</span>{/if}
            {#if word instanceof Num}<span style="color: var(--grey-70)">- </span><span style="font-size: .85em">{word.num}</span>{/if}
            <!-- @formatter:on-->
        </div>

        <div class="cell translation no-break">{@html translations}</div>

        <!-- TODO remove redundant element and handle via css column -->
        <div class="list-spacer-cell"></div>

        <div class="cell level">
            {#if word.base.level.toString() !== 'NOLEVEL'}
                {word.base.level.toString()}
            {/if}
        </div>

        <div class="cell frequency">
            {word.base.frequency}
        </div>

        <ListRowButton
                buttonType="known"
                loggedIn={loggedIn}
                status={status}
                on:click={() => handleRowButtonClick(Status.Known)}
        />

        <ListRowButton
                buttonType="learn"
                loggedIn={loggedIn}
                status={status}
                on:click={() => handleRowButtonClick(Status.Learn)}
        />

    </div>

{/key}

<style>

    .first-english {
        border-top: 2px solid black;
        /*background: lightblue;*/
    }

    .row {
        font-size: 1rem;
        height: 100%;
        display: grid;
        align-items: flex-end;
        cursor: pointer;
        user-select: none;
        -webkit-user-select: none;
        padding: .25rem 12px .25rem 20px;
        border-bottom: 1px solid var(--color-dark);
        grid-template-columns: minmax(15ch, 1fr) 1.8fr .6rem 1rem .9rem 1.8rem 1.6rem;
    }

    .row:hover {
        background: var(--color-hover);
    }

    .current-row {
        background: var(--color-light);
    }

    .current-word {
        font-weight: var(--font-emph);
    }

    .level,
    .frequency {
        margin-left: auto;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        color: var(--grey-50);
        line-height: 1;
        font-size: .85em;
        border-radius: 3px;
    }

    .frequency {
        color: var(--color-accent);
    }

    .cell {
        position: relative;
    }

    .frequency:hover::before {
        content: 'Frequency';
    }

    .level:hover::before {
        content: 'Level';
    }


    .frequency:hover::before,
    .level:hover::before {
        position: absolute;
        left: 50%;
        top: 0;
        transform: translate(-50%, calc(-100% - 5px));
        background-color: var(--color-accent);
        color: white;
        padding: .4rem;
        border-radius: 3px;
        box-shadow: var(--shadow);
        font-weight: bold;
        pointer-events: none;
    }

    .no-break {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    @media (max-width: 1000px) {
        .row {
            font-size: .95rem;
            grid-template-columns: minmax(14ch, .8fr) 1.8fr .1rem 1rem 1.8rem 1.6rem;
            padding: .25rem 12px .25rem 1rem;
        }

        .frequency {
            display: none;

        }
    }

</style>